// Event stock allocations — Supabase implementation.
//
// qty_sold is NOT writable from here. It moves only inside
// create_transaction() and create_return(), which hold a row lock and are
// checked by the alloc_not_oversold constraint. The old incrementSold /
// decrementSold helpers are therefore gone: the browser has no business
// adjusting stock counters directly.

import { supabase } from '@/lib/supabase';
import { mapAllocation } from './mappers';
import { msToIsoDate } from '@/lib/format';
import type { EventStockAllocation } from '@/types/domain';

export async function listEventAllocations(
  eventId: string | null,
): Promise<EventStockAllocation[]> {
  if (!eventId) return [];
  const { data, error } = await supabase
    .from('event_stock_allocations')
    .select('*')
    .eq('event_id', eventId)
    .order('product_name');
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapAllocation);
}

export async function getEventAllocation(
  eventId: string,
  productId: string,
): Promise<EventStockAllocation | null> {
  const { data, error } = await supabase
    .from('event_stock_allocations')
    .select('*')
    .eq('event_id', eventId)
    .eq('product_id', productId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapAllocation(data) : null;
}

export interface SetAllocationInput {
  eventId: string;
  qtyIn: number;
  reason?: string;
  /**
   * Batch identity. Two allocations of the same product in one event are
   * distinguished by (lotNumber, expiryDate) — leave both unset and the stock
   * lands in the single "no lot recorded" batch, which is how every allocation
   * behaved before lots existed.
   */
  lotNumber?: string;
  /** ms epoch. Undefined for products that do not expire. */
  expiryDate?: number;
  /** Packaging label — pcs, box, botol. Defaults to the product's own. */
  unit?: string;
  /** Order-form reference. Undefined leaves an existing value untouched. */
  orderForm?: string;
  /** Either the id directly… */
  productId?: string;
  /** …or the product object the picker already has to hand. */
  product?: { id: string; sku?: string; name?: string; price?: number };
  /**
   * Accepted but ignored — the actor comes from auth.uid() server-side.
   * Retained so existing call sites compile.
   */
  createdBy?: string;
  userDisplayName?: string;
  userEmail?: string;
}

/**
 * Upsert how many units are carried to an event. Rejected by the database if
 * qtyIn would fall below what has already sold.
 */
export async function setEventAllocation(
  input: SetAllocationInput,
): Promise<EventStockAllocation> {
  const productId = input.productId ?? input.product?.id;
  if (!productId) throw new Error('productId wajib diisi');
  // Note: sku/name/price on `input.product` are ignored. The snapshot stored
  // on the allocation is read from the products table inside the function, so
  // a stale or tampered client copy cannot set the selling price.
  const { data, error } = await supabase.rpc('set_event_allocation', {
    p_event_id: input.eventId,
    p_product_id: productId,
    p_qty_in: input.qtyIn,
    p_reason: input.reason ?? undefined,
    p_lot_number: input.lotNumber?.trim() ?? '',
    // The RPC takes a date, not a timestamp: an expiry is a calendar day, and
    // sending an instant shifts it across the timezone boundary. msToIsoDate is
    // the exact inverse of isoDateToMs — see the note in lib/format.ts for what
    // goes wrong when the two halves disagree.
    p_expiry_date: input.expiryDate ? msToIsoDate(input.expiryDate) : undefined,
    p_unit: input.unit?.trim() || undefined,
    p_order_form: input.orderForm !== undefined ? input.orderForm.trim() : undefined,
  });
  if (error) throw new Error(error.message);
  return mapAllocation(data as never);
}

export interface RemoveAllocationInput {
  eventId: string;
  productId: string;
  /** Preferred. Without it every lot of the product would be removed. */
  allocationId?: string;
  /** Accepted but ignored — actor and reason are recorded server-side. */
  removedBy?: string;
  reason?: string;
  userDisplayName?: string;
  userEmail?: string;
}

export async function removeEventAllocation({
  eventId,
  productId,
  allocationId,
}: RemoveAllocationInput): Promise<void> {
  // Delete the batch when one is named. Falling back to (event, product) would
  // now delete every lot of that product, which is never what the button meant.
  let q = supabase.from('event_stock_allocations').delete();
  if (allocationId) q = q.eq('id', allocationId);
  else q = q.eq('event_id', eventId).eq('product_id', productId);
  const { error } = await q;
  if (error) throw new Error(error.message);
}

export interface AllocationReturnAuditEntry {
  id: string;
  action: string;
  entityId: string;
  productId?: string;
  productName?: string;
  productSku?: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  reason?: string | null;
  loss?: number;
  userId: string;
  userEmail?: string | null;
  userDisplayName?: string | null;
  createdAt: number;
}

/**
 * Allocation change history for an event — the "History" tab.
 *
 * Reads the real audit trail rather than reconstructing it: write_audit()
 * records before/after on every set_event_allocation and close_event call, so
 * this shows who changed what and why, not just the current numbers.
 */
export async function listEventAllocationAuditEntries(
  eventId: string,
  limit = 100,
): Promise<AllocationReturnAuditEntry[]> {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .eq('event_id', eventId)
    .in('entity', ['allocation', 'event'])
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);

  // Product names are not stored on the audit row, so resolve them from the
  // current allocations — cheap, and already cached by the caller's query.
  const allocations = await listEventAllocations(eventId);
  const byProduct = new Map(allocations.map((a) => [a.productId, a]));

  return (data ?? []).map((r) => {
    const alloc = r.entity_id ? byProduct.get(r.entity_id) : undefined;
    const after = (r.after ?? null) as Record<string, unknown> | null;
    return {
      id: r.id,
      action: r.action,
      entityId: r.entity_id ?? '',
      productId: r.entity_id ?? undefined,
      productName: alloc?.productName,
      productSku: alloc?.productSku,
      before: (r.before ?? null) as Record<string, unknown> | null,
      after,
      reason: (after?.reason as string | undefined) ?? null,
      loss: alloc ? alloc.qtyIn - alloc.qtySold - alloc.qtyReturnedToGudang : undefined,
      userId: r.actor_id ?? '',
      userEmail: r.actor_email,
      userDisplayName: r.actor_email,
      createdAt: new Date(r.created_at).getTime(),
    };
  });
}

export interface UpdateAllocationBatchInput {
  allocationId: string;
  /** Needed only for cache invalidation; the RPC addresses the row by id. */
  eventId: string;
  qtyIn?: number;
  lotNumber?: string;
  /** ms epoch. */
  expiryDate?: number;
  /** True to remove an existing expiry — undefined alone means "leave as is". */
  clearExpiry?: boolean;
  unit?: string;
  /** '' clears it; undefined leaves it. */
  orderForm?: string;
  reason?: string;
}

/**
 * Edit an existing batch in place — the owner's rule: everything is editable
 * until the batch sells, then only the quantity.
 *
 * Deliberately NOT setEventAllocation. That one is an upsert keyed on the
 * batch identity, so handing it a corrected lot number would find no match and
 * insert a second row, leaving the original behind. This addresses the row by
 * id, so a corrected lot is still the same row.
 *
 * The rule itself is enforced by a trigger (alloc_guard_batch), not here: a
 * sold batch refuses lot/expiry/unit changes for any writer, and the error it
 * raises names the lot and how many sold, so it is passed straight through.
 */
export async function updateAllocationBatch(
  input: UpdateAllocationBatchInput,
): Promise<EventStockAllocation> {
  const { data, error } = await supabase.rpc('update_allocation_batch', {
    p_id: input.allocationId,
    p_qty_in: input.qtyIn ?? undefined,
    p_lot_number: input.lotNumber !== undefined ? input.lotNumber.trim() : undefined,
    p_expiry_date: input.expiryDate ? msToIsoDate(input.expiryDate) : undefined,
    p_clear_expiry: input.clearExpiry ?? false,
    p_unit: input.unit?.trim() || undefined,
    p_reason: input.reason ?? undefined,
    p_order_form: input.orderForm !== undefined ? input.orderForm.trim() : undefined,
  });
  if (error) throw new Error(error.message);
  return mapAllocation(data as never);
}
